// Admin "Customers" — new admin nav item (customers/products/users
// multi-select bulk-actions task). Every organization with name/TIN/tier/
// status/contact/requests/quotations/value-won/last-activity, sortable and
// searchable. Clicking a row opens CustomerDetailPage.jsx (full request +
// quotation history, contact people). Checkbox-per-row + a bulk action bar
// (deactivate/reactivate/delete) — same components/hooks CatalogPage.jsx
// and UsersPage.jsx use for the identical requirement on products/users.
//
// Ledger-style: one ruled table, no card-per-row, no sidebar (root
// CLAUDE.md) — same shape as AllRequestsPage/CatalogPage.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminCustomers,
  useBulkDeactivateCustomers,
  useBulkReactivateCustomers,
  useBulkDeleteCustomers,
  fetchCustomerBulkDeletePreview,
} from '../hooks/useAdminCustomers.js';
import { useRowSelection } from '../hooks/useRowSelection.js';
import { useDebouncedValue } from '../utils/useDebouncedValue.js';
import { confirmAndBulkAction, confirmAndBulkDelete } from '../utils/bulkActions.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import BulkActionBar from '../components/BulkActionBar.jsx';
import { formatDate } from '../utils/formatDate.js';
import { formatMoney } from 'shared/money.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './CustomersPage.module.css';

const SORT_COLUMNS = [
  { key: 'name', labelId: 'customers.col.name' },
  { key: 'tin', labelId: 'customers.col.tin' },
  { key: 'tier', labelId: 'customers.col.tier' },
  { key: 'status', labelId: 'customers.col.status' },
  { key: 'requestCount', labelId: 'customers.col.requests' },
  { key: 'quotationCount', labelId: 'customers.col.quotations' },
  { key: 'totalValueWon', labelId: 'customers.col.valueWon' },
  { key: 'lastActivity', labelId: 'customers.col.lastActivity' },
];

function SortableHeader({ column, sortBy, sortDir, onSort, t }) {
  const active = sortBy === column.key;
  return (
    <th>
      <button type="button" className={styles.sortButton} onClick={() => onSort(column.key)}>
        {t(column.labelId)}
        {active &&
          (sortDir === 'desc' ? (
            <ArrowDown size={14} aria-hidden="true" />
          ) : (
            <ArrowUp size={14} aria-hidden="true" />
          ))}
      </button>
    </th>
  );
}

export default function CustomersPage() {
  const { t, lang } = useLanguage();
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const debouncedSearch = useDebouncedValue(searchInput, 250);
  const search = debouncedSearch.trim() || undefined;

  const {
    data: customers,
    isLoading,
    isError,
    error,
    refetch,
  } = useAdminCustomers({
    search,
    sortBy,
    sortDir,
  });
  const allCustomers = customers || [];
  const isTrulyEmpty = !isLoading && !isError && allCustomers.length === 0 && !search;
  const isFilteredEmpty = !isLoading && !isError && allCustomers.length === 0 && Boolean(search);

  const selection = useRowSelection(allCustomers);
  const { confirm, toastOk, toastErr } = useActionFeedback();
  const bulkDeactivate = useBulkDeactivateCustomers();
  const bulkReactivate = useBulkReactivateCustomers();
  const bulkDelete = useBulkDeleteCustomers();

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const handleBulkDeactivate = () =>
    confirmAndBulkAction({
      ids: selection.selectedIds,
      confirm,
      t,
      titleId: 'bulk.deactivateConfirmTitle',
      messageId: 'bulk.deactivateConfirmBody',
      confirmLabelId: 'bulk.deactivateConfirmButton',
      actionFn: (ids) => bulkDeactivate.mutateAsync(ids),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
      knownErrorCodes: [],
    });

  const handleBulkReactivate = () =>
    confirmAndBulkAction({
      ids: selection.selectedIds,
      confirm,
      t,
      titleId: 'bulk.reactivateConfirmTitle',
      messageId: 'bulk.reactivateConfirmBody',
      confirmLabelId: 'bulk.reactivateConfirmButton',
      actionFn: (ids) => bulkReactivate.mutateAsync(ids),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
      knownErrorCodes: [],
    });

  const handleBulkDelete = () =>
    confirmAndBulkDelete({
      ids: selection.selectedIds,
      confirm,
      t,
      previewFn: fetchCustomerBulkDeletePreview,
      deleteFn: (ids, confirmedCount) => bulkDelete.mutateAsync({ ids, confirmedCount }),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
      knownErrorCodes: [],
    });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('customers.title')}</h1>
      <p className={styles.subtitle}>{t('customers.subtitle')}</p>

      <div className={styles.filterRow}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('customers.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      <BulkActionBar
        count={selection.count}
        onClear={selection.clear}
        actions={[
          {
            id: 'deactivate',
            labelId: 'bulk.deactivate',
            onClick: handleBulkDeactivate,
            pending: bulkDeactivate.isPending,
          },
          {
            id: 'reactivate',
            labelId: 'bulk.reactivate',
            onClick: handleBulkReactivate,
            pending: bulkReactivate.isPending,
          },
          {
            id: 'delete',
            labelId: 'bulk.delete',
            tone: 'danger',
            onClick: handleBulkDelete,
            pending: bulkDelete.isPending,
          },
        ]}
      />

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="customers.errorBody" error={error} onRetry={refetch} />
      )}

      {isTrulyEmpty && <EmptyState titleId="customers.emptyTitle" bodyId="customers.emptyBody" />}
      {isFilteredEmpty && (
        <EmptyState titleId="customers.emptyTitle" bodyId="customers.emptyBody" />
      )}

      {!isLoading && !isError && allCustomers.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  aria-label={t('bulk.selectAll')}
                  checked={selection.allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selection.someSelected;
                  }}
                  onChange={selection.toggleAll}
                />
              </th>
              {SORT_COLUMNS.map((column) => (
                <SortableHeader
                  key={column.key}
                  column={column}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                  t={t}
                />
              ))}
              <th>{t('customers.col.contact')}</th>
              <th>{t('customers.col.email')}</th>
              <th>{t('customers.col.phone')}</th>
            </tr>
          </thead>
          <tbody>
            {allCustomers.map((org) => (
              <tr
                key={org.id}
                className={`${styles.row} ${org.status === 'suspended' ? styles.rowInactive : ''}`}
              >
                <td className={styles.checkboxCell}>
                  <input
                    type="checkbox"
                    checked={selection.isSelected(org.id)}
                    onChange={() => selection.toggle(org.id)}
                    aria-label={org.name}
                  />
                </td>
                <td>
                  <Link to={`/customers/${org.id}`} className={styles.nameLink}>
                    {lang === 'am' && org.name_am ? org.name_am : org.name}
                  </Link>
                </td>
                <td className="tabularNums">{org.tin || '—'}</td>
                <td>{t(`customers.tier.${org.tier}`)}</td>
                <td>
                  <span
                    className={
                      org.status === 'suspended' ? styles.statusInactive : styles.statusActive
                    }
                  >
                    {t(`customers.status.${org.status}`)}
                  </span>
                </td>
                <td className="tabularNums">{org.request_count}</td>
                <td className="tabularNums">{org.quotation_count}</td>
                <td className="tabularNums">{formatMoney(org.total_value_won)}</td>
                <td className="tabularNums">
                  {formatDate(org.last_activity_at, lang) || t('customers.neverActive')}
                </td>
                <td>{org.contact_name}</td>
                <td>{org.contact_email || '—'}</td>
                <td>{org.contact_phone || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
